import React from 'react'
import {Stack, Box, StackProps, useMultiStyleConfig, Flex, Text, TextProps, BoxProps} from '@chakra-ui/react'
import {Button, ButtonProps, Picture} from '_uiComponents'
import {FormattedMessage} from 'react-intl'
import {useAnalytics} from '_customHooks'
import {useModal} from '_context'
import {EventNewsletterLabel} from '_types'

interface NewsletterRegistrationProps {
    title?: string
    description?: string
    localeID?: string
    isMobile?: boolean
    mobileImage?: {
        url?: string
        alt?: string
    }
    analyticsLabel: EventNewsletterLabel
}

/**
 * @name NewsletterRegistrationStep3
 *
 * @component
 *
 * @description
 * Renders step 3 of Newsletter Modal.
 *
 * @param {NewsletterRegistrationProps} props - Properties object
 * @param {string} [props.title] - Title for the locale modal.
 * @param {string} [props.description] - Description for the locale modal.
 * @param {string} [props.localeID] - Locale id format it-it.
 * @param {boolean} [props.isMobile] - boolean to render mobile components.
 * @param {{url?: string; alt?: string}} [props.mobileImage] - Mobile image setup for both URL and alt properties.
 * @param {string} [props.analyticsLabel] - Newsletter analytics labels type.
 *
 * @returns {React.FC<NewsletterRegistrationProps>} Rendered Newsletter Modal Step 3.
 */
const NewsletterRegistrationStep3: React.FC<NewsletterRegistrationProps> = ({title, analyticsLabel, isMobile = false, mobileImage, localeID}) => {
    const styles = useMultiStyleConfig('NewsletterModal_step_3') as Record<string, StackProps & TextProps & BoxProps>
    const {closeModal} = useModal()
    const {analyticsEventNewsletter} = useAnalytics()

    const handleNotNow = () => {
        analyticsEventNewsletter({action: 'not_now', label: analyticsLabel})
        closeModal()
    }

    const handleRegistrationRedirect = () => {
        analyticsEventNewsletter({action: 'go_to_registration', label: analyticsLabel})
    }

    return (
        <Stack data-testid="email-input-nl-2" {...styles.containerstep_3}>
            {isMobile && <Picture objectFit="cover" positionContainer="relative" position="absolute" imagexs={mobileImage?.url} alt={mobileImage?.alt} />}
            <Box {...styles.titleWrapperThankyou}>
                <Text>{title}</Text>
            </Box>
            <Box {...styles.description}>
                <Box as={Text}>
                    <FormattedMessage id="label.newsletter.form.thanks.textprefs" defaultMessage="The preferences have been successfully updated." />
                </Box>
                <Box as="span">
                    <FormattedMessage
                        id="label.newsletter.typ.registration"
                        defaultMessage="Create an account to enjoy a personalized experience and discover all the exclusive services reserved for you."
                    />
                </Box>
            </Box>
            <Flex {...styles.buttonsWrapper}>
                <Button
                    {...(styles.button as ButtonProps)}
                    as={'a'}
                    onClick={handleRegistrationRedirect}
                    href={`/${localeID}/registration` + (localStorage.getItem('cu') ? '?cu=' + localStorage.getItem('cu') : '')}
                    variant="primary"
                    size="newsletter"
                    type="button"
                >
                    <FormattedMessage id="title.save.customer.information" defaultMessage="Create an account" />
                </Button>
                <Button type="button" onClick={handleNotNow} {...(styles.buttonNotNow as ButtonProps)} variant="textLink" size="md">
                    <FormattedMessage id="label.button.notNow" defaultMessage="Not Now" />
                </Button>
            </Flex>
        </Stack>
    )
}

export {NewsletterRegistrationStep3}
